type StarRatingProps = {
  rating: number;
  max?: number;
  size?: 'sm' | 'md';
  className?: string;
};

function StarIcon({ filled, className }: { filled: boolean; className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 20 20"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={filled ? 0 : 1.5}
      aria-hidden
    >
      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
    </svg>
  );
}

export function StarRating({ rating, max = 5, size = 'sm', className = '' }: StarRatingProps) {
  const safeRating = Math.max(0, Math.min(max, Math.round(rating)));
  const iconClass = size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4';

  return (
    <div
      className={`flex items-center gap-0.5 ${className}`}
      role="img"
      aria-label={`${rating.toFixed(1)} out of ${max} stars`}
    >
      {Array.from({ length: max }).map((_, index) => (
        <StarIcon
          key={index}
          filled={index < safeRating}
          className={`${iconClass} ${index < safeRating ? 'text-amber-400' : 'text-slate-600'}`}
        />
      ))}
    </div>
  );
}

export function StarRatingBar({
  rating,
  max = 5,
  className = '',
}: {
  rating: number;
  max?: number;
  className?: string;
}) {
  const safeRating = Math.max(0, Math.min(max, rating));
  const fillWidth = `${(safeRating / max) * 100}%`;
  const iconClass = 'h-4 w-4 shrink-0';

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <div className="relative flex h-4 w-[92px]" aria-hidden>
        <div className="absolute inset-0 flex">
          {Array.from({ length: max }).map((_, index) => (
            <StarIcon key={`empty-${index}`} filled={false} className={`${iconClass} text-slate-600`} />
          ))}
        </div>
        <div className="absolute inset-0 flex overflow-hidden" style={{ width: fillWidth }}>
          {Array.from({ length: max }).map((_, index) => (
            <StarIcon key={`full-${index}`} filled className={`${iconClass} text-amber-400`} />
          ))}
        </div>
      </div>
      <span className="sr-only">{safeRating.toFixed(1)} out of {max} stars</span>
    </div>
  );
}
